import MuiList from '@mui/material/List';
import MuiListItem from '@mui/material/ListItem';
import MuiListItemButton from '@mui/material/ListItemButton';
import React from 'react';

import { Collapse } from '../collapse';
import { Divider } from '../divider';
import { IconExpandLess, IconExpandMore } from '../icon';
import { toArray } from '../utils';
import { Item, ItemProps } from './item';

type ListItemSpecificProps<R = undefined> = {
    item?: R;
    disableRipple?: boolean;
    children?: ListItemProps<R>[] | ListItemProps<R>;
    onClick?(item: R): void;
    secondaryAction?: React.ReactNode;
    onMouseEnter?(event: React.MouseEvent<HTMLDivElement, MouseEvent>): void;
};

export type ListItemProps<R = undefined> = ListItemSpecificProps<R> & ItemProps;

export const ListItem = React.forwardRef(
    <R,>(props: ListItemProps<R>, ref: React.ForwardedRef<HTMLDivElement>) => {
        const {
            item,
            divider,
            children,
            onMouseEnter,
            onClick,
            disableRipple,
            secondaryAction,
            sx,
            className,
            'data-role': dataRole,
            ...itemProps
        } = props;

        const [open, setOpen] = React.useState(props.selected);

        const handleClick = () => {
            if (children) {
                setOpen((prevState: boolean | undefined) => !prevState);
            }
            onClick?.(item!);
        };

        React.useEffect(() => {
            if (props.selected) {
                setOpen(true);
            }
        }, [props.selected]);

        if (children) {
            return (
                <>
                    {divider === 'top' && <Divider />}
                    <div style={{ flexShrink: 0 }}>
                        <MuiListItemButton
                            ref={ref}
                            disabled={props.disabled}
                            disableRipple={disableRipple}
                            selected={
                                props.selected && !toArray(children)?.length
                            }
                            onClick={handleClick}
                            onMouseEnter={onMouseEnter}
                            sx={sx}
                            className={className}
                            data-role={dataRole}
                        >
                            <Item {...itemProps} />
                            {Boolean(toArray(children)?.length) &&
                                (open ? (
                                    <IconExpandLess />
                                ) : (
                                    <IconExpandMore />
                                ))}
                        </MuiListItemButton>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <MuiList component="div" disablePadding>
                                {toArray(children).map((nestedItem, index) => (
                                    <ListItem
                                        key={index}
                                        {...nestedItem}
                                        onClick={
                                            nestedItem.children
                                                ? undefined
                                                : (item: R) => {
                                                      nestedItem.onClick?.(
                                                          item
                                                      );
                                                  }
                                        }
                                        sx={nestedItem.sx ?? sx}
                                    />
                                ))}
                            </MuiList>
                        </Collapse>
                    </div>
                    {divider === 'bottom' && <Divider />}
                </>
            );
        }

        if (onClick) {
            return (
                <>
                    {divider === 'top' && <Divider />}
                    <MuiListItemButton
                        ref={ref}
                        disabled={props.disabled}
                        disableRipple={disableRipple}
                        selected={props.selected}
                        onClick={() => onClick(item!)}
                        onMouseEnter={onMouseEnter}
                        sx={sx}
                        className={className}
                        data-role={dataRole}
                    >
                        <Item {...itemProps} />
                    </MuiListItemButton>
                    {divider === 'bottom' && <Divider />}
                </>
            );
        }

        return (
            <>
                {divider === 'top' && <Divider />}
                <MuiListItem
                    secondaryAction={secondaryAction}
                    sx={sx}
                    className={className}
                    data-role={dataRole}
                >
                    <Item {...itemProps} />
                </MuiListItem>
                {divider === 'bottom' && <Divider />}
            </>
        );
    }
);

(ListItem as React.FunctionComponent).displayName = 'ListItem';
